<script lang="ts">
	import Formatted from "@components/common/Formatted.svelte";
	import { getCookie } from "@utils/getCookie.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import type { SearchMethodParams, SearchResponse } from "algoliasearch";
	import type { Hit, LiteClient } from "algoliasearch/lite";
	import type { AnyIndexable, Indexable } from "@core/schema/Indexable.js";
	import { type IndexableTable, isIndexableTable } from "@core/schema/IndexableTable.js";
	import { PricingLevel } from "@core/schema/PricingLevel.js";
	import { indexableTables } from "@core/schema/indexableTables.js";
	import { getPostDetailPath } from "@core/schema/paths/getPostDetailPath.js";
	import { getProductDetailPath } from "@core/schema/paths/getProductDetailPath.js";
	import { getAlgoliaIndexName } from "@core/schema/utils/getAlgoliaIndexName.js";
	import { isCountryInEuropeanUnion } from "@core/schema/utils/isCountryInEuropeanUnion.js";
	import { isHitOfIndexable } from "@core/schema/utils/isHitOfIndexable.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { pricingLevelSecretKeyCookieName } from "../../pricingLevelSecretKeyCookieName.js";
	import { selectedCurrency } from "../../state/selectedCurrency.svelte.js";
	import { selectedDestinationCountry } from "../../state/selectedDestinationCountry.svelte.js";
	import translations from "./AlgoliaResults.translations.json" with { type: "json" };

	const { searchText }: { searchText: string } = $props();

	const { tenant, subcategories } = getGlobalTemplateProps();
	const detailPathGetterMap: { [K in IndexableTable]: (hit: Hit<Indexable<K>>) => string | null } = {
		post: (hit) => getPostDetailPath({ urlSlug: hit._data.urlSlug, locale: tenant.locale }),
		product: (hit) => {
			const subcategory = subcategories.find(({ id }) => id === hit._data.subcategoryId);
			if (!subcategory) {
				return null;
			}
			return getProductDetailPath(
				tenant.locale,
				{ urlSlug: hit._data.urlSlug, variants: [], attributeGroups: [] },
				subcategory,
				null,
				[],
			);
		},
	};

	let nextSearch = "";
	let canMakeRequest = true;
	let results = $state.raw<SearchResponse<AnyIndexable>[]>([]);
	let client: LiteClient | undefined;

	const { ALGOLIA_API_SEARCH_KEY, ALGOLIA_APPLICATION_ID } = getGlobalSettings();

	const search = createPromiseWithStatus(async (params: SearchMethodParams) => {
		if (!client) {
			const { liteClient } = await import("algoliasearch/lite");
			// eslint-disable-next-line require-atomic-updates
			client = liteClient(ALGOLIA_APPLICATION_ID, ALGOLIA_API_SEARCH_KEY);
		}
		return client.searchForHits<AnyIndexable>(params);
	});

	async function onSearchTextChange(text: string): Promise<void> {
		nextSearch = text;
		if (!canMakeRequest) {
			return;
		}

		if (text.length <= 2) {
			results = [];
			return;
		}

		try {
			canMakeRequest = false;
			({ results } = await search.call({
				requests: [
					{
						indexName: getAlgoliaIndexName(
							tenant.locale,
							(await getCookie(pricingLevelSecretKeyCookieName)) ?? PricingLevel.B2c,
						),
						query: text,
					},
				],
			}));
		} finally {
			window.setTimeout(() => {
				canMakeRequest = true;
				if (nextSearch !== text) {
					void onSearchTextChange(nextSearch);
				}
			}, 250);
		}
	}

	function getHighlightedText<T extends AnyIndexable>(hit: T, key: Extract<keyof T, string>): string {
		const { _highlightResult } = hit;
		if (!_highlightResult) {
			return "";
		}
		const property = _highlightResult[key];
		if (!property || !("value" in property) || typeof property.value !== "string") {
			return "";
		}

		return property.value;
	}

	$effect(() => {
		void onSearchTextChange(searchText);
	});

	const hits = $derived(
		results
			.flatMap(({ hits }) => hits)
			.map((hit) => {
				const [tag] = hit._tags;
				const detailPath = detailPathGetterMap[tag](hit as never);
				if (!hit.image || detailPath === null) {
					return null;
				}
				return { hit, detailPath };
			})
			.filter((hit) => hit !== null)
			.sort((a, b) => {
				const aIndex = isIndexableTable(a.hit._tags[0]) ? indexableTables.indexOf(a.hit._tags[0]) : -1;
				const bIndex = isIndexableTable(b.hit._tags[0]) ? indexableTables.indexOf(b.hit._tags[0]) : -1;
				return aIndex - bIndex;
			}),
	);

	function getPriceLabel(product: Hit<Indexable<"product">>): string {
		const currency = selectedCurrency.current ?? tenant.defaultCurrency;
		const destinationCountry = selectedDestinationCountry.current;
		if (destinationCountry === null) {
			throw new Error("Destination country not initialized");
		}
		const country = isCountryInEuropeanUnion(destinationCountry) ? destinationCountry : "outsideEu";
		return product._data.prices[`${currency}-${country}`];
	}

	const translate = getTranslate(translations);
	/* eslint-disable svelte/no-at-html-tags */
</script>

{#if searchText.length <= 2}
	<div class="flex items-center h-12 overflow-hidden">
		<div class="text-xs/tight grow">
			<Formatted text={translate("resultsWillAppear")} />
		</div>
	</div>
{:else if hits.length === 0}
	<div class="flex items-center h-12 overflow-hidden">
		<div class="text-xs/tight grow">
			<Formatted text={translate("notFound")} />
		</div>
	</div>
{:else}
	{#each hits as { hit, detailPath } (hit.objectID)}
		<a
			class="flex first:rounded-t-xl last:rounded-b-lg py-4 px-2 gap-2 items-center hover:bg-fcrlightgray/50 overflow-hidden transition-colors shrink-0"
			href={detailPath}
		>
			<img
				src={hit.image}
				alt={"title" in hit ? hit.title : hit.name}
				width={108}
				height={60}
				class="rounded-xl w-27 h-15 shrink-0 object-cover"
			/>
			<div class="flex flex-col max-w-[calc(100%-7rem)]">
				<div class="text-base font-antipol [&_em]:text-fcrbrown [&_em]:not-italic grow truncate">
					{#if "title" in hit}
						{@html getHighlightedText(hit, "title")}
					{:else}
						{@html getHighlightedText(hit, "name")}
					{/if}
				</div>
				<div>
					{#if isHitOfIndexable(hit, "post")}
						<p
							class="text-sm/4 font-medium text-fcrgray-200 [&_em]:text-fcrdarkgray [&_em]:not-italic grow line-clamp-2"
						>
							{@html getHighlightedText(hit, "perex")}
						</p>
					{:else}
						<div class="text-sm/4 font-medium text-fcrgray-200 [&_em]:text-fcrdarkgray [&_em]:not-italic grow">
							{getPriceLabel(hit)}
						</div>
					{/if}
				</div>
			</div>
		</a>
	{/each}
{/if}
